import { useCallback, useEffect, useRef, useState } from 'react';
import { staffAuthApi } from '../lib/api';
import { SITES, STAFF_SITE_KEYS, parseHash, staffSiteUrl, siteByKey, startPageUrl } from '../lib/sites';
import { getStaffToken, setStaffToken, clearStaffToken } from '../lib/staffSession';

// The staff start page as the one place staff sign in (lib/staffSession.js).
// A staff site with no sign-in sends the browser here with #return=<site key>;
// signed in, this hands the sign-in back to that site with a one-time code.
// StaffHome hooks in with:
//
//   const hub = useStaffHub();
//   if (hub.screen) return hub.screen;  // a site is waiting: sign in, or "Opening…"
//   {hub.panel}                         // "Signed in as …" or the sign-in form
export default function useStaffHub() {
  const [hash, setHash] = useState(() => window.location.hash);
  const [token, setToken] = useState(() => getStaffToken('staff'));
  const [me, setMe] = useState(null);
  const [notice, setNotice] = useState('');
  const [problem, setProblem] = useState('');
  const handingOff = useRef('');

  const { page, params } = parseHash(hash);
  const target = STAFF_SITE_KEYS.includes(params.get('return')) ? siteByKey(params.get('return')) : null;
  const state = params.get('state') || '';
  const arrivedSignedOut = params.has('signedout');

  useEffect(() => {
    const onHash = () => { setHash(window.location.hash); setProblem(''); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const forget = useCallback(() => {
    clearStaffToken('staff');
    setToken('');
    setMe(null);
  }, []);

  // Arriving from "Sign out" on another site: that sign-out already ended
  // this sign-in on the server; forget it here too.
  useEffect(() => {
    if (!arrivedSignedOut) return;
    forget();
    setNotice("You're signed out of every staff site in this browser.");
    window.history.replaceState(null, '', window.location.pathname + window.location.search + (page ? `#${page}` : ''));
    setHash(window.location.hash);
  }, [arrivedSignedOut, page, forget]);

  // Who is signed in, and which sites that login opens.
  useEffect(() => {
    if (!token || target) return undefined;
    let cancelled = false;
    staffAuthApi.session(token)
      .then((m) => { if (!cancelled) setMe(m); })
      .catch((e) => { if (!cancelled && e.status === 401) forget(); });
    return () => { cancelled = true; };
  }, [token, target, forget]);

  // A site asked for a sign-in and we have one: send it straight back.
  useEffect(() => {
    if (!token || !target || handingOff.current === `${target.key}:${state}`) return;
    handingOff.current = `${target.key}:${state}`;
    staffAuthApi.handoff(token, target.key)
      .then(({ code }) => window.location.replace(staffSiteUrl(target, { handoff: code, state })))
      .catch((e) => {
        handingOff.current = '';
        if (e.status === 401) forget();
        else if (e.status === 403) setProblem(`Your login doesn't include ${target.name}. Ask Olaf or Alet if you need it.`);
        else setProblem(e.message || `Couldn't open ${target.name}.`);
      });
  }, [token, target, state, forget]);

  function signedIn(t, admin) {
    setStaffToken('staff', t);
    setNotice('');
    setMe(admin);
    setToken(t);
  }

  async function signOut() {
    const t = token;
    forget();
    await staffAuthApi.logout(t).catch(() => {});
    setNotice("You're signed out of every staff site in this browser.");
  }

  let screen = null;
  if (target) {
    screen = (
      <div style={s.center}>
        {!token ? (
          <SignInForm heading={`Sign in to open ${target.name}`} onSignedIn={signedIn} notice={notice} />
        ) : problem ? (
          <div style={s.box}>
            <img src="/logo.png" alt="Silver Service" style={s.boxLogo} />
            <p style={s.problem}>{problem}</p>
            <a href={staffSiteUrl(target)} style={s.primaryLink}>Try again</a>
            <a href={startPageUrl()} style={s.quietLink}>See all sites</a>
          </div>
        ) : (
          <p style={s.quiet}>Opening {target.name}…</p>
        )}
      </div>
    );
  }

  const opens = me && me.sites ? SITES.filter((site) => me.sites.includes(site.key)).map((site) => site.name) : [];
  const panel = (
    <div style={s.panel}>
      {token ? (
        <>
          <div style={s.who}>
            {me ? <>Signed in as <strong>{me.name || me.email}</strong>{me.permission ? ` · ${me.permission}` : ''}</> : 'Signed in'}
            {opens.length > 0 && <div style={s.opens}>Your login opens {opens.join(', ')}.</div>}
          </div>
          <button type="button" style={s.outline} onClick={signOut} title="Signs this browser out of every staff site">Sign out</button>
        </>
      ) : (
        <SignInForm inline heading="Sign in once for every staff site" onSignedIn={signedIn} notice={notice} />
      )}
    </div>
  );

  return { screen, panel };
}

export function SignInForm({ heading, onSignedIn, notice, inline }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { token, admin } = await staffAuthApi.login(email.trim(), password);
      setPassword('');
      onSignedIn(token, admin);
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form style={inline ? s.inlineForm : s.box} onSubmit={submit}>
      {!inline && <img src="/logo.png" alt="Silver Service" style={s.boxLogo} />}
      <div style={inline ? s.inlineHeading : s.boxHeading}>{heading}</div>
      {notice && <div style={s.notice}>{notice}</div>}
      <div style={inline ? s.inlineFields : s.fields}>
        <input style={inline ? s.inlineInput : s.input} type="email" placeholder="you@silverservice.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="username" autoFocus={!inline} required />
        <input style={inline ? s.inlineInput : s.input} type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" required />
        <button style={s.primary} type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
      </div>
      {error && <div style={s.err}>{error}</div>}
      {!inline && <div style={s.hint}>One sign-in covers every staff site.</div>}
    </form>
  );
}

const RED = '#A50B18';
const s = {
  panel: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: '#fff', border: '1px solid #e3e3e3', borderRadius: 12, padding: '14px 16px', marginBottom: 20 },
  who: { color: '#333', fontSize: '0.92rem' },
  opens: { color: '#777', fontSize: '0.82rem', marginTop: 3 },
  outline: { background: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.85rem', cursor: 'pointer' },
  inlineForm: { display: 'flex', flexDirection: 'column', gap: 8, width: '100%' },
  inlineHeading: { color: '#222', fontWeight: 600, fontSize: '0.95rem' },
  inlineFields: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  inlineInput: { flex: '1 1 200px', minWidth: 0, padding: '0.65rem 0.9rem', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.92rem', outline: 'none', boxSizing: 'border-box' },

  center: { minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' },
  box: { background: '#fff', borderRadius: 12, padding: '2rem', maxWidth: 400, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 14, boxSizing: 'border-box' },
  boxLogo: { height: 40, alignSelf: 'center' },
  boxHeading: { color: '#222', fontWeight: 700, fontSize: '1.15rem', textAlign: 'center' },
  fields: { display: 'flex', flexDirection: 'column', gap: 12 },
  input: { minWidth: 0, padding: '0.65rem 0.9rem', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.92rem', outline: 'none', boxSizing: 'border-box' },
  primary: { background: RED, color: '#fff', border: 'none', borderRadius: 8, padding: '0.65rem 1.4rem', fontSize: '0.92rem', fontWeight: 600, cursor: 'pointer' },
  primaryLink: { display: 'block', textAlign: 'center', background: RED, color: '#fff', textDecoration: 'none', borderRadius: 8, padding: '0.7rem 1rem', fontWeight: 600, fontSize: '0.92rem' },
  quietLink: { color: '#888', fontSize: '0.85rem', textAlign: 'center' },
  quiet: { color: '#888', fontSize: '0.95rem', margin: 0 },
  problem: { color: '#444', fontSize: '0.92rem', margin: 0, lineHeight: 1.5, textAlign: 'center' },
  notice: { color: '#2d6a3e', background: '#eef7f0', border: '1px solid #cfe6d5', borderRadius: 8, padding: '0.55rem 0.8rem', fontSize: '0.85rem' },
  err: { color: '#c0392b', fontSize: '0.85rem', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: 6, padding: '0.55rem 0.8rem' },
  hint: { color: '#999', fontSize: '0.8rem', textAlign: 'center' },
};
